import type { FC } from "react";
import { useTranslation } from "@privacy/translations";
import { useForm, Controller } from "react-hook-form";
import Typography from "@mui/material/Typography";
import type { DpiaById } from "../../api/dpia.types";
import * as FormDialog from "../../shared/FormDialog";
import { UserSelect } from "../../shared/UserSelect";
import { useGetUsersFromCurrentOrganisation } from "../../api/users";
import { updateDpiaStatusMutationOptions } from "../../api/dpia";
import { useMutation } from "@tanstack/react-query";

export const SendForDecisionDialog: FC<{ dpia: DpiaById; onClose: () => void }> = ({ dpia, onClose }) => {
  const { t } = useTranslation();
  const { handleSubmit, control } = useForm<{ approverId: string | null }>({
    defaultValues: { approverId: dpia.approverId },
  });
  const updateDpiaStatusMutation = useMutation(updateDpiaStatusMutationOptions());
  const { data } = useGetUsersFromCurrentOrganisation();

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("DpiaDrawer.Dialog.SendForApproval.Header")}</FormDialog.Title>
      <FormDialog.Form
        onSubmit={handleSubmit(({ approverId }) => {
          if (!approverId) throw new Error("Approver is required");
          updateDpiaStatusMutation.mutate(
            { dpiaId: dpia.id, status: "waitingForDecision", approverId },
            { onSuccess: onClose }
          );
        })}
      >
        <FormDialog.Content>
          <FormDialog.FormError isError={updateDpiaStatusMutation.isError} />
          <Typography
            sx={{
              pb: 2,
              fontSize: theme => theme.typography.pxToRem(14),
              color: theme => theme.palette.text.secondary,
            }}
          >
            {t("DpiaDrawer.Dialog.SendForApproval.Body")}
          </Typography>
          <FormDialog.Fields>
            <Controller
              control={control}
              name="approverId"
              rules={{ required: true }}
              render={({ field: { ref, onChange, value }, fieldState }) => (
                <FormDialog.Field errorMessage={fieldState.error && t("Input.Validation.Required")}>
                  <UserSelect
                    enableCreateNewUser
                    error={Boolean(fieldState.error)}
                    selectedId={value}
                    ref={ref}
                    users={data?.users}
                    onSelectUser={onChange}
                  />
                </FormDialog.Field>
              )}
            />
          </FormDialog.Fields>
        </FormDialog.Content>
        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={updateDpiaStatusMutation.isPending}>
            {t("DpiaDrawer.Dialog.SendForApproval.ConfirmButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
